import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Alert, Empty, Table } from 'antd';
import { withRouter } from 'react-router-dom';
import VoiceSubheader from '../VoiceSubheader';
import followUpColumns from './followUpColumns';
import FollowUpFilters from './FollowUpFilters';
import FollowUpDetailDrawer from './FollowUpDetailDrawer';
import useFollowUpQueue from './useFollowUpQueue';
import './voiceFollowUps.scss';

const VoiceFollowUps = ({
  reset,
  setReset,
  history,
  location,
}) => {
  const subheaderRef = useRef(null);
  const queue = useFollowUpQueue({
    history,
    location,
    reset,
    setReset,
  });
  const emptyDescription = queue.filters.lifecycle === 'open' ? 'No open follow-ups' : 'No follow-ups found';
  const selectFollowUp = (followup) => {
    const selectedText = window.getSelection().toString();
    if (selectedText) return;

    queue.selectFollowUp(followup.id);
  };
  const selectedFollowUpIndex = queue.data.findIndex((followup) => String(followup.id) === String(queue.selectedFollowUpId));
  const canNavigatePrevious = selectedFollowUpIndex > 0;
  const canNavigateNext = selectedFollowUpIndex >= 0 && selectedFollowUpIndex < queue.data.length - 1;
  const navigateFollowUp = (direction) => {
    const nextIndex = selectedFollowUpIndex + direction;
    const nextFollowUp = queue.data[nextIndex];
    if (!nextFollowUp) return;

    queue.selectFollowUp(nextFollowUp.id);
  };

  return (
    <div className="voice-follow-ups">
      <VoiceSubheader ref={subheaderRef}>
        <FollowUpFilters
          filters={queue.filters}
          options={queue.options}
          optionsError={queue.optionsError}
          onChange={queue.updateFilters}
          onClear={queue.handleClear}
          onAssigneeSearch={queue.scheduleAssigneeSearch}
        />
      </VoiceSubheader>
      {queue.listError && <Alert message={queue.listError} type="error" showIcon />}
      <Table
        columns={followUpColumns}
        dataSource={queue.data}
        rowKey={(followup) => followup.id}
        loading={queue.loading}
        sticky={{
          offsetHeader: subheaderRef.current?.clientHeight,
        }}
        pagination={{
          current: queue.pagination.current,
          pageSize: queue.pagination.pageSize,
          total: queue.pagination.total,
          pageSizeOptions: [10, 25, 50],
          showSizeChanger: true,
          showTotal: (total, range) => `Viewing ${range[0]}-${range[1]} of ${total} follow-ups`,
          position: ['bottomCenter'],
        }}
        locale={{
          emptyText: queue.listError ? null : <Empty description={emptyDescription} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
        }}
        onChange={queue.handleTableChange}
        onRow={(followup) => ({
          role: 'button',
          tabIndex: 0,
          'aria-label': `Open follow-up for ${followup.caller_name || 'unknown caller'}`,
          onClick: () => selectFollowUp(followup),
          onKeyDown: (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;

            event.preventDefault();
            selectFollowUp(followup);
          },
        })}
      />
      <FollowUpDetailDrawer
        followUpId={queue.selectedFollowUpId}
        onClose={queue.closeDrawer}
        onFollowUpChanged={queue.handleFollowUpChanged}
        onNavigatePrevious={() => navigateFollowUp(-1)}
        onNavigateNext={() => navigateFollowUp(1)}
        canNavigatePrevious={canNavigatePrevious}
        canNavigateNext={canNavigateNext}
      />
    </div>
  );
};

VoiceFollowUps.defaultProps = {
  reset: false,
  setReset: () => {},
  history: {},
  location: { pathname: '/app/voice_inbox', search: '' },
};

VoiceFollowUps.propTypes = {
  reset: PropTypes.bool,
  setReset: PropTypes.func,
  history: PropTypes.object,
  location: PropTypes.object,
};

export default withRouter(VoiceFollowUps);
